import React, { memo, ReactNode } from 'react';
import { cm } from 'src/utils/classNames';
import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { Checkmark } from '@velocity-exchange/icons';
import { WithChildren, WithClassName } from 'src/utils/UtilityTypes';
import { DefinitionTooltipID } from 'src/components/Tooltip/DefinitionTooltip/definitionTooltipConfig';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import Tooltip, {
	TooltipProps,
} from 'src/components/primitives/Tooltip/Tooltip';
import { getIsMediaHover } from 'src/utils/browser';

type Size = 'sm' | 'md';

interface BaseCheckboxDefaultProps {
	id: string;
	/**
	 * Only affects the text size.
	 */
	size?: Size;
	checked: boolean;
	onCheckedChange?: () => void;
	label: ReactNode;
	className?: string;
	labelClassName?: string;
	disabled?: boolean;
	/**
	 * Test ID for Playwright/testing
	 */
	dataTestId?: string;
}

type BaseTooltipOptions = {
	side?: TooltipProps['side'];
};

type DefinitionTooltipOptions = {
	definitionId: DefinitionTooltipID;
	content?: never;
};

type ContentTooltipOptions = {
	content: TooltipProps['content'];
	definitionId?: never;
};

type TooltipOptions = BaseTooltipOptions &
	(DefinitionTooltipOptions | ContentTooltipOptions);

type CheckboxDefaultProps = BaseCheckboxDefaultProps & {
	tooltip?: TooltipOptions;
};

/**
 * Default configuration of the `Checkbox` component.
 * You can build your own with `Row`, `Check`, and `Label` if you need more customization.
 */
function CheckboxDefault({
	id,
	size = 'sm',
	checked,
	onCheckedChange,
	label,
	className,
	labelClassName,
	disabled,
	tooltip,
	dataTestId,
}: CheckboxDefaultProps) {
	const labelElement = (
		<CheckboxLabel
			id={id}
			className={labelClassName}
			size={size}
			disabled={disabled}
		>
			{label}
		</CheckboxLabel>
	);

	const content = (() => {
		if (!tooltip?.content && !tooltip?.definitionId) {
			return labelElement;
		}

		const { definitionId, content, side } = tooltip;

		if (definitionId) {
			return (
				<DefinitionTooltip definitionId={definitionId} side={side} asChild>
					{labelElement}
				</DefinitionTooltip>
			);
		}

		return (
			<Tooltip content={content} side={side} asChild>
				{labelElement}
			</Tooltip>
		);
	})();

	return (
		<CheckboxRow className={className} disabled={disabled}>
			<CheckboxCheck
				id={id}
				checked={checked}
				onCheckedChange={onCheckedChange}
				disabled={disabled}
				data-testid={dataTestId}
			/>
			{content}
		</CheckboxRow>
	);
}

interface CheckboxCheckProps extends RadixCheckbox.CheckboxProps {
	/**
	 * `id` links the label with the checkbox.
	 */
	id: string;
	disabled?: boolean;
	'data-testid'?: string;
}

function CheckboxCheck({
	checked,
	className,
	disabled,
	...rest
}: CheckboxCheckProps) {
	return (
		<RadixCheckbox.Root
			className={cm(
				'flex size-lg shrink-0 cursor-pointer items-center justify-center rounded-2xs border transition-colors',
				'border-text-secondary bg-input-bg',
				'disabled:cursor-not-allowed disabled:border-button-disabled disabled:bg-button-disabled',
				!disabled &&
					'data-[state=checked]:border-purple-link data-[state=checked]:bg-purple-link',
				className
			)}
			disabled={disabled}
			checked={checked}
			{...rest}
		>
			<RadixCheckbox.Indicator className="text-main-bg data-disabled:text-text-disabled">
				<Checkmark size={14} className="relative top-0.5" />
			</RadixCheckbox.Indicator>
		</RadixCheckbox.Root>
	);
}

export interface CheckboxLabelProps extends WithChildren, WithClassName {
	id: string;
	size?: Size;
	disabled?: boolean;
}

function CheckboxLabel({
	id,
	size,
	className,
	disabled,
	children,
	// Spread props to ensure Tooltip can be triggered if it exists. Radix passes
	// button element props so we can't type this unless we also use a button.
	...rest
}: CheckboxLabelProps) {
	// Programmatically toggle the checkbox to work around HeadlessUI's focus-visible
	// management interfering with native `htmlFor` behavior on mobile (ridiculous).
	const handleClick = (e: React.MouseEvent<HTMLLabelElement>) => {
		if (disabled || getIsMediaHover()) {
			return;
		}

		const checkbox = document.getElementById(id);
		if (checkbox) {
			e.preventDefault();
			checkbox.click();
		}
	};

	return (
		<label
			className={cm(
				'cursor-pointer whitespace-nowrap text-text-secondary select-none',
				size === 'sm' ? 'typo-body-xs' : 'typo-body-sm',
				disabled && 'cursor-not-allowed text-text-disabled',
				className
			)}
			htmlFor={id}
			onClick={handleClick}
			{...rest}
		>
			{children}
		</label>
	);
}

interface CheckboxRowProps extends WithChildren, WithClassName {
	disabled?: boolean;
}

function CheckboxRow({ children, disabled, className }: CheckboxRowProps) {
	return (
		<div
			className={cm(
				// `relative` crucial here, as Radix applies absolute positioning and a
				// transform to the checkbox `input`, which in some cases may break layout.
				// See: https://github.com/radix-ui/primitives/issues/2778 for an example.
				'relative flex cursor-pointer items-start gap-x-sm',
				disabled && 'cursor-not-allowed',
				className
			)}
		>
			{children}
		</div>
	);
}

export default {
	/**
	 * Default configuration of the `Checkbox` component.
	 * You can build your own with `Row`, `Check`, and `Label` if you need more customization.
	 */
	Default: memo(CheckboxDefault),
	Label: memo(CheckboxLabel),
	Row: memo(CheckboxRow),
	Check: memo(CheckboxCheck),
};
